import type React from "react";
import { useState } from "react";
import { PostCard, type PostCardViewModel } from "@/components/common/PostCard";
import { EventLinkPreviewProvider } from "@/contexts/EventLinkPreviewContext";
import { CommentSection } from "@/features/study-group-stream/components/CommentSection";
import { useCommentToggleFallback } from "@/features/study-group-stream/hooks/useCommentToggleFallback";
import type { EventApiResponse } from "@/lib/humhub-api/types";

interface PostThreadProps {
  postCard: PostCardViewModel;
  commentSectionProps: React.ComponentProps<typeof CommentSection>;
  eventLinkPreviewProps?: {
    onEventClick?: (eventData: EventApiResponse) => void;
    currentStudyGroupId?: string;
  };
  className?: string;
  canComment?: boolean;
}

export const PostThread: React.FC<PostThreadProps> = ({
  postCard,
  commentSectionProps,
  eventLinkPreviewProps,
  className,
  canComment = true,
}) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const {
    isCommentActive,
    isComposerOpen,
    commentSectionRef,
    handleCommentToggle,
    closeComposer,
  } = useCommentToggleFallback({
    onCommentToggle: postCard.handlers.onCommentToggle,
  });
  const fallbackControlledComposer = !postCard.handlers.onCommentToggle;
  return (
    <article
      data-post-content-id={postCard.ids.postContentId}
      data-activity-key={`post:${postCard.ids.postContentId}`}
      className={`relative w-full overflow-hidden rounded-3xl border border-slate-200 bg-white ${className ?? ""}`}
    >
      {postCard.status.isPinned && (
        <span
          aria-hidden="true"
          className="absolute left-0 top-5 z-10 h-8 w-[3px] rounded-r-full bg-rw-main"
        />
      )}
      <PostCard
        {...postCard}
        status={{
          ...postCard.status,
          isThreadRoot: true,
          isCommentActive: postCard.status.isCommentActive || isCommentActive,
          isMinimized,
        }}
        handlers={{
          ...postCard.handlers,
          onCommentToggle: canComment ? handleCommentToggle : undefined,
          onToggleMinimize: () => setIsMinimized((prev) => !prev),
        }}
      />
      {!isMinimized && (
        <div ref={commentSectionRef}>
          <EventLinkPreviewProvider
            onEventClick={eventLinkPreviewProps?.onEventClick}
            currentStudyGroupId={eventLinkPreviewProps?.currentStudyGroupId}
          >
            <CommentSection
              {...commentSectionProps}
              openComposer={
                canComment && fallbackControlledComposer
                  ? isComposerOpen
                  : canComment && commentSectionProps.openComposer
              }
              onCommentSent={
                fallbackControlledComposer
                  ? () => {
                      commentSectionProps.onCommentSent?.();
                      closeComposer();
                    }
                  : commentSectionProps.onCommentSent
              }
            />
          </EventLinkPreviewProvider>
        </div>
      )}
    </article>
  );
};
